/**
 * Traducción al español de los errores de validación que devuelve FastAPI/Pydantic v2.
 *
 * Pydantic devuelve cada error como `{loc, msg, type, ctx, input}`. `type` es un código
 * estable (no cambia entre versiones menores), así que mapeamos por `type` + `ctx` en vez de
 * regex sobre `msg` (más frágil).
 */

const FIELD_LABELS: Record<string, string> = {
  // Triaje / signos vitales
  motivo_consulta: "Motivo de consulta",
  nivel_urgencia: "Nivel de urgencia",
  sede_id: "Sede",
  especialidad_sugerida_id: "Área / especialidad sugerida",
  pa_sistolica: "Presión sistólica",
  pa_diastolica: "Presión diastólica",
  fc: "Frecuencia cardiaca",
  fr: "Frecuencia respiratoria",
  temperatura: "Temperatura",
  sat_o2: "Saturación de O₂",
  peso: "Peso",
  talla: "Talla",
  observaciones: "Observaciones",

  // Paciente / persona
  paciente_id: "Paciente",
  tipo_documento: "Tipo de documento",
  num_documento: "Número de documento",
  nombres: "Nombres",
  ap_paterno: "Apellido paterno",
  ap_materno: "Apellido materno",
  fecha_nacimiento: "Fecha de nacimiento",
  sexo: "Sexo",
  ubigeo: "Ubigeo",
  direccion: "Dirección",
  email: "Correo electrónico",
  telefono: "Teléfono",
  grupo_sanguineo: "Grupo sanguíneo",
  alergias: "Alergias",
  antecedentes: "Antecedentes",

  // Profesional / asignación / cita
  profesional_id: "Profesional",
  especialidad_id: "Especialidad",
  colegiatura: "Colegiatura",
  tipo_colegio: "Tipo de colegio",
  rne: "RNE",
  motivo: "Motivo",
  inicio: "Inicio",
  fin: "Fin",
  fecha: "Fecha",
  estado: "Estado",

  // Tenant / usuario
  username: "Usuario",
  password: "Contraseña",
  ruc: "RUC",
  razon_social: "Razón social",
  nombre_comercial: "Nombre comercial",
  slug: "Identificador (slug)",
  color_primario: "Color de marca",
  plan_codigo: "Plan",
  regimen_tributario: "Régimen tributario",
  rol_ids: "Roles",
};

function fieldLabel(loc: unknown[]): string {
  const path = loc
    .filter((x) => x !== "body" && x !== "query" && x !== "path")
    .map((x) => (typeof x === "string" ? x : String(x)));
  const last = path[path.length - 1] ?? "";
  return FIELD_LABELS[last] ?? path.join(".");
}

interface ValidationItem {
  loc?: unknown[];
  msg?: string;
  type?: string;
  ctx?: Record<string, unknown>;
}

function translateValidationItem(item: ValidationItem): string {
  const field = item?.loc ? fieldLabel(item.loc) : "";
  const ctx = item?.ctx ?? {};
  const type = item?.type ?? "";

  let body: string;
  switch (type) {
    case "missing":
      body = "es obligatorio";
      break;
    case "string_too_short":
      body = `debe tener al menos ${ctx.min_length} caracteres`;
      break;
    case "string_too_long":
      body = `no puede tener más de ${ctx.max_length} caracteres`;
      break;
    case "greater_than_equal":
      body = `debe ser mayor o igual a ${ctx.ge}`;
      break;
    case "less_than_equal":
      body = `debe ser menor o igual a ${ctx.le}`;
      break;
    case "greater_than":
      body = `debe ser mayor que ${ctx.gt}`;
      break;
    case "less_than":
      body = `debe ser menor que ${ctx.lt}`;
      break;
    case "int_type":
    case "int_parsing":
      body = "debe ser un número entero";
      break;
    // Llega un decimal donde se esperaba entero. Sin este caso salía el texto
    // en inglés de Pydantic tal cual en pantalla.
    case "int_from_float":
      body = "debe ser un número entero, sin decimales";
      break;
    case "float_type":
    case "float_parsing":
    case "decimal_type":
    case "decimal_parsing":
      body = "debe ser un número";
      break;
    case "bool_type":
    case "bool_parsing":
      body = "debe ser verdadero o falso";
      break;
    case "string_type":
      body = "debe ser texto";
      break;
    case "date_type":
    case "date_parsing":
      body = "debe ser una fecha válida (YYYY-MM-DD)";
      break;
    case "datetime_type":
    case "datetime_parsing":
      body = "debe ser una fecha y hora válidas";
      break;
    case "enum":
      body = `valor no permitido${ctx.expected ? ` (valores válidos: ${ctx.expected})` : ""}`;
      break;
    case "value_error":
      // Errores lanzados desde validators del modelo. Pydantic prefija con "Value error, ".
      body = (item.msg ?? "valor inválido").replace(/^Value error,\s*/i, "");
      break;
    case "string_pattern_mismatch":
      body = "el formato no es válido";
      break;
    default:
      // Fallback: usa el msg original si no reconocemos el type
      body = item.msg ?? "es inválido";
  }
  return field ? `${field}: ${body}` : body;
}

export function translateValidationErrors(detail: unknown): string | null {
  if (!Array.isArray(detail)) return null;
  const parts = (detail as ValidationItem[]).map(translateValidationItem);
  return parts.length > 0 ? parts.join(". ") : null;
}
